import { getPageImage } from "@/lib/page-imagery";
import { QuotationWorkbookDownload } from "@/components/insights/QuotationWorkbookDownload";
import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, TableWrap, thClass, tdClass } from "@/components/insights/prose";

export const compareQuotationsMeta: InsightMeta = {
  slug: "compare-iso-certification-quotes-iraq",
  metaTitle: {
    en: "Compare ISO Certification Quotations in Iraq | IraqCert",
    ar: "مقارنة عروض أسعار شهادات الأيزو في العراق | عراق سيرت",
  },
  title: {
    en: "How to Compare ISO Certification Quotations in Iraq",
    ar: "كيف تقارن عروض أسعار شهادات الأيزو في العراق؟",
  },
  description: {
    en: "Compare ISO certification offers for your Iraqi company by scope, sites, issuing body and included work before agreeing a private quotation.",
    ar: "قارن عروض شهادات الأيزو لشركتك في العراق وفق النطاق والمواقع والجهة المانحة والأعمال المشمولة قبل الاتفاق على عرض السعر.",
  },
  datePublished: "2026-09-23",
  dateModified: "2026-09-27",
  dateLabel: {
    en: "27 September 2026",
    ar: "27 أيلول 2026",
  },
  enquiryTitle: {
    en: "Discuss your certification scope",
    ar: "ناقش نطاق الشهادة المطلوبة",
  },
  enquiryButton: {
    en: "Discuss your certification scope",
    ar: "ناقش نطاق الشهادة المطلوبة",
  },
  enquiryMessage: {
    en: "Hello IraqCert, I read your guide to comparing ISO certification quotations. I would like to discuss the scope of support for my company.\nStandard:\nBusiness activity and sites:\nBuyer requirement:\nPage: /insights/compare-iso-certification-quotes-iraq",
    ar: "مرحباً عراق سيرت، قرأت دليل مقارنة عروض أسعار شهادات الأيزو، وأرغب في مناقشة نطاق الدعم الذي تحتاجه شركتي.\nالمواصفة المطلوبة:\nنشاط الشركة ومواقعها:\nمتطلب الجهة المشترية:\nالصفحة: /ar/insights/compare-iso-certification-quotes-iraq",
  },
  image: getPageImage("/insights/compare-iso-certification-quotes-iraq", "editorial"),
  keyPoints: [
    {
      en: "Compare the same activities and sites before comparing totals.",
      ar: "قارن الأنشطة والمواقع نفسها قبل مقارنة الإجماليات.",
    },
    {
      en: "Separate preparation support from the certification body’s work.",
      ar: "افصل دعم التحضير عن أعمال جهة منح الشهادات.",
    },
    {
      en: "Identify later audit work, exclusions and separately quoted items.",
      ar: "حدّد أعمال التدقيق اللاحقة والاستثناءات والبنود المسعّرة بصورة منفصلة.",
    },
  ],
  sections: [
    {
      id: "same-basis",
      label: {
        en: "What to compare",
        ar: "بنود المقارنة",
      },
    },
    {
      id: "different-offers",
      label: {
        en: "Why the work differs",
        ar: "لماذا تختلف الأعمال؟",
      },
    },
    {
      id: "factory-example",
      label: {
        en: "Factory and warehouse example",
        ar: "مثال المصنع والمستودع",
      },
    },
    {
      id: "agree-offer",
      label: {
        en: "Before accepting the offer",
        ar: "قبل قبول العرض",
      },
    },
  ],
  faq: [
    {
      q: {
        en: "Can I compare ISO certification offers only by total price?",
        ar: "هل يمكن مقارنة عروض شهادات الأيزو بالسعر الإجمالي وحده؟",
      },
      a: {
        en: "Compare the total after aligning the standard, activities, sites and included work. Ask for clarification where an offer leaves the issuer, audit arrangements or later services unspecified.",
        ar: "قارن الإجمالي بعد توحيد المواصفة والأنشطة والمواقع والأعمال المشمولة. واطلب التوضيح إذا لم يحدد العرض الجهة المانحة أو ترتيبات التدقيق أو الخدمات اللاحقة.",
      },
    },
    {
      q: {
        en: "Is consultancy included in the certification quotation?",
        ar: "هل يشمل عرض سعر الشهادة الخدمات الاستشارية؟",
      },
      a: {
        en: "Do not assume so. Ask for preparation support and certification-body work to be identified separately, with the organization responsible for each. The written offer should make any exclusions clear.",
        ar: "لا تفترض ذلك. اطلب تحديد دعم التحضير وأعمال جهة منح الشهادات بشكل منفصل، مع بيان الجهة المسؤولة عن كل منهما. وينبغي أن يوضح العرض المكتوب أي استثناءات.",
      },
    },
    {
      q: {
        en: "Does accreditation guarantee that my buyer will accept the certificate?",
        ar: "هل يضمن الاعتماد قبول الشهادة لدى المشتري؟",
      },
      a: {
        en: "No. Check the buyer’s exact conditions, including the standard, scope, validity and any named bodies. Accreditation alone does not guarantee vendor approval or a contract award.",
        ar: "لا. راجع شروط المشتري بدقة، ومنها المواصفة والنطاق والصلاحية وأي جهات محددة بالاسم. ولا يضمن الاعتماد وحده قبول المورد أو ترسية العقد.",
      },
    },
  ],
};

const comparisonRows = [
  [
    {
      en: "Standard and buyer requirement",
      ar: "المواصفة ومتطلب الجهة المشترية",
    },
    {
      en: "Offers may address different objectives.",
      ar: "قد تستهدف العروض احتياجات مختلفة.",
    },
    {
      en: "Does each offer address the exact requirement I received?",
      ar: "هل يعالج كل عرض نص المتطلب الذي تلقيته؟",
    },
  ],
  [
    {
      en: "Activities and sites",
      ar: "الأنشطة والمواقع",
    },
    {
      en: "A narrower scope changes the comparison.",
      ar: "اختلاف نطاق التغطية يغيّر أساس المقارنة.",
    },
    {
      en: "Which operations and locations are included?",
      ar: "ما العمليات والمواقع المشمولة؟",
    },
  ],
  [
    {
      en: "Preparation and certification",
      ar: "التحضير ومنح الشهادة",
    },
    {
      en: "These are different responsibilities.",
      ar: "لكل منهما مسؤوليات مختلفة.",
    },
    {
      en: "Who prepares the system, who audits it, and what is excluded?",
      ar: "من يهيّئ النظام ومن يدققه، وما الأعمال المستثناة؟",
    },
  ],
  [
    {
      en: "Issuer and accreditation",
      ar: "الجهة المانحة والاعتماد",
    },
    {
      en: "The proposed route must match any stated buyer condition.",
      ar: "ينبغي أن يتوافق مسار المنح المقترح مع أي شرط يحدده المشتري.",
    },
    {
      en: "Which body will issue the certificate and, if accreditation is required, what accredited scope applies?",
      ar: "ما الجهة التي ستصدر الشهادة، وما نطاق اعتمادها إن كان الاعتماد مطلوباً؟",
    },
  ],
  [
    {
      en: "Initial and ongoing work",
      ar: "الأعمال الأولية واللاحقة",
    },
    {
      en: "An initial offer may omit later services.",
      ar: "قد لا يشمل العرض الأولي الخدمات اللاحقة.",
    },
    {
      en: "Are audit stages, surveillance, renewal and travel included or separately quoted?",
      ar: "هل يشمل العرض مراحل التدقيق والمتابعة والتجديد والسفر، أم تُسعّر منفصلة؟",
    },
  ],
];

export function CompareQuotationsBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "Two ISO offers can carry the same standard on the first page and cover different work. One may include help preparing your company. Another may begin with the certification audit and assume that preparation is complete. The lower total tells you very little until those responsibilities are clear.",
          ar: "قد يحمل عرضان للأيزو اسم المواصفة نفسها في الصفحة الأولى، بينما يشملان أعمالاً مختلفة. فقد يتضمن أحدهما المساعدة في تحضير شركتك، ويبدأ الآخر بتدقيق منح الشهادة على افتراض اكتمال التحضير. لا يخبرك الإجمالي الأقل بالكثير ما لم تتضح هذه المسؤوليات.",
        })}
      </P>
      <P>
        {t({
          en: "Start by giving each provider the same brief: the buyer’s written requirement, the activities and sites involved, and the system your company already has. ISO recommends evaluating several certification bodies and checking their practices and accreditation. A common brief makes their answers easier to compare.",
          ar: "ابدأ بإرسال المعلومات نفسها إلى كل مقدّم عرض: متطلب المشتري المكتوب، والأنشطة والمواقع المعنية، والنظام القائم في شركتك. توصي منظمة ISO بتقييم عدة جهات لمنح الشهادات والتحقق من ممارساتها واعتمادها. وإرسال وصف موحّد للاحتياج يجعل مقارنة إجاباتها أوضح.",
        })}{" "}
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "ISO’s guidance on choosing a certification body",
            ar: "إرشادات ISO لاختيار جهة منح الشهادات",
          })}
        </a>
      </P>
      <H2 id="same-basis">
        {t({ en: "Put the offers on the same basis", ar: "قارن العروض على أساس موحّد" })}
      </H2>
      <P>
        {t({
          en: "Use the questions below to expose differences in the work. Record an unanswered question as something to clarify with the provider; an empty line in the offer does not tell you whether the item is included.",
          ar: "استخدم الأسئلة التالية لتحديد الاختلافات بين الأعمال المشمولة. وسجّل أي سؤال بلا إجابة لطلب توضيحه من مقدّم العرض؛ فغياب البند لا يبيّن إن كان مشمولاً.",
        })}
      </P>
      <TableWrap>
        <thead className="bg-accent">
          <tr>
            <th scope="col" className={thClass}>
              {t({ en: "Compare", ar: "بند المقارنة" })}
            </th>
            <th scope="col" className={thClass}>
              {t({ en: "Why it matters", ar: "أهميته" })}
            </th>
            <th scope="col" className={thClass}>
              {t({ en: "Ask before accepting", ar: "ما الذي تسأل عنه قبل القبول؟" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {comparisonRows.map((row) => (
            <tr key={row[0].en} className="border-t border-border">
              {row.map((cell, index) =>
                index === 0 ? (
                  <th
                    scope="row"
                    key={cell.en}
                    className={`${tdClass} font-semibold text-foreground`}
                  >
                    {t(cell)}
                  </th>
                ) : (
                  <td key={cell.en} className={tdClass}>
                    {t(cell)}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <QuotationWorkbookDownload variant="comparison" />

      <H2 id="different-offers">
        {t({
          en: "Why two quotations can legitimately differ",
          ar: "لماذا قد يختلف عرضان لأسباب مبرّرة؟",
        })}
      </H2>
      <P>
        {t({
          en: "For quality, environmental and occupational health and safety management systems, IAF MD 5:2023 links audit-time planning to personnel and other factors, including the operation’s complexity or risk. Headcount alone is insufficient. This document does not set a market price, and its rules should not be assumed to apply identically to every ISO standard.",
          ar: "يربط IAF MD 5:2023 تخطيط وقت تدقيق أنظمة إدارة الجودة والبيئة والصحة والسلامة المهنية بعدد العاملين وعوامل أخرى، منها تعقيد العمليات أو مخاطرها. ولا يكفي عدد العاملين وحده. لا تحدد هذه الوثيقة سعراً سوقياً، ولا ينبغي افتراض انطباق قواعدها بالطريقة نفسها على جميع مواصفات الأيزو.",
        })}{" "}
        <a
          href="https://iaf.nu/iaf_system/uploads/documents/IAF_MD5_Issue_4_Version_3_14062023.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({ en: "IAF MD 5 audit-time guidance", ar: "وثيقة IAF MD 5 لتحديد وقت التدقيق" })}
        </a>
      </P>
      <P>
        {t({
          en: "Those differences should be visible in the quotation. Ask who prepares the system, who conducts the audit and which work your team must complete. If a provider assumes that records, internal audits or corrective work are already in place, establish that before agreeing the timetable.",
          ar: "ينبغي أن تظهر هذه الاختلافات في عرض السعر. اسأل عمّن يهيّئ النظام، ومن يجري التدقيق، وما الأعمال التي يجب أن يُكملها فريقك. وإذا افترض مقدّم العرض أن السجلات أو التدقيق الداخلي أو الأعمال التصحيحية مكتملة، فاحسم ذلك قبل الاتفاق على الجدول.",
        })}{" "}
        <LangLink
          to="/insights/iso-consultant-vs-certification-body-in-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "Preparation support versus the certification body’s role",
            ar: "الفرق بين دعم التحضير ودور جهة منح الشهادات",
          })}
        </LangLink>
      </P>
      <H2 id="factory-example">
        {t({
          en: "A food producer’s factory and separate warehouse",
          ar: "مصنع منشأة غذائية ومستودعها المنفصل",
        })}
      </H2>
      <P>
        {t({
          en: "Consider a food producer with a factory and a separate warehouse. One offer describes manufacturing at the factory. The second includes storage and dispatch at the warehouse as well. Comparing the totals immediately would hide a material difference in the proposed work. First establish which operations and locations the customer expects to see covered, then ask both providers to quote that scope.",
          ar: "لنفترض منشأة غذائية لديها مصنع ومستودع منفصل. يصف عرضٌ التصنيع داخل المصنع، ويشمل العرض الثاني التخزين وإرسال البضائع من المستودع أيضاً. مقارنة الإجماليين مباشرةً تخفي اختلافاً جوهرياً في الأعمال المقترحة. حدّد أولاً العمليات والمواقع التي يتوقع العميل شمولها، ثم اطلب من الجهتين تسعير النطاق نفسه.",
        })}{" "}
        <LangLink
          to="/insights/iso-certification-scope-activities-sites-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "Understand certificate scope, activities and sites",
            ar: "فهم نطاق الشهادة والأنشطة والمواقع المشمولة",
          })}
        </LangLink>
      </P>
      <H2 id="agree-offer">
        {t({
          en: "Resolve differences before accepting an offer",
          ar: "وضّح الاختلافات قبل قبول العرض",
        })}
      </H2>
      <P>
        {t({
          en: "Ask for the final offer to record the agreed scope, responsibilities, exclusions and later work. Changes agreed on a call need to appear there too. Where the buyer requires accreditation, verify the proposed issuer and relevant scope before committing. The certification body makes the certification decision; the buyer assesses whether the resulting certificate meets its requirement.",
          ar: "اطلب أن يوثّق العرض النهائي النطاق والمسؤوليات والاستثناءات والأعمال اللاحقة المتفق عليها. وينبغي أن تظهر فيه أيضاً التعديلات التي اتُّفق عليها خلال المكالمات. وإذا اشترط المشتري الاعتماد، فتحقّق من الجهة المانحة المقترحة والنطاق المعني قبل الالتزام. تتخذ جهة منح الشهادات قرار المنح، ويقيّم المشتري مدى استيفاء الشهادة لمتطلبه.",
        })}{" "}
        <LangLink
          to="/insights/accredited-vs-non-accredited-iso-certificate"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "Check what accreditation does and does not establish",
            ar: "ما الذي يثبته الاعتماد وما الذي لا يضمنه؟",
          })}
        </LangLink>
      </P>
      <P>
        {t({
          en: "For an initial enquiry to IraqCert, share the standard, business activity, sites and buyer wording. We can discuss the preparation support and coordination you need; the independent certification body retains the audit and certification decision. Our cost and process guide explains how to frame a new quotation request.",
          ar: "للاستفسار الأولي لدى عراق سيرت، اذكر المواصفة ونشاط الشركة ومواقعها ونص متطلب المشتري. يمكننا مناقشة دعم التحضير والتنسيق الذي تحتاجه؛ وتبقى مسؤولية التدقيق وقرار منح الشهادة لدى جهة المنح المستقلة. يوضح دليل التكلفة والإجراءات كيفية إعداد طلب عرض سعر جديد.",
        })}{" "}
        <LangLink
          to="/insights/iso-certification-cost-and-process-in-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "ISO quotation scope and process in Iraq",
            ar: "نطاق عرض سعر الأيزو وإجراءاته في العراق",
          })}
        </LangLink>
      </P>
    </>
  );
}
